import { computed, makeAutoObservable, observable, action } from "mobx";
import { injectable } from "inversify";
import sortBy from "lodash/sortBy";
import type { TableColumn, ColumnOption } from "@whatslly/ui-components/v2/Tables/Table/TableTypes";
import type { IEditColumnsStore } from "../types";
@injectable()
export class EditColumnsStore implements IEditColumnsStore {
  columns: ColumnOption[] = [];
  defaultColumns: string[] = [];
  selectedColumns: string[] = [];
  tempSelectedColumns: string[] = [];
  columnOrder: string[] = [];
  isOpen: boolean = false;
  search: string = "";
  tableColumns?: TableColumn<any>[];
  customColumnNames: Record<string, string> = {};
  originalColumnTitles: Record<string, string> = {};
  currentStorageKey: string | null = null;

  constructor() {
    makeAutoObservable(
      this,
      {
        currentStorageKey: observable,
        sortedColumns: computed,
      },
      { autoBind: true }
    );
  }

  initialize(storageKey: string, tableColumns: Array<TableColumn<any>>) {
    this.currentStorageKey = storageKey;

    const newColumns = this.mapTableColumns(tableColumns);
    const newColumnsKeys = newColumns.map((col) => col.key);

    const originalColumnTitles: Record<string, string> = {};
    tableColumns.forEach((col) => {
      originalColumnTitles[col.keyInsideItem] = col.title || "";
    });

    const nonOptionalColumns = newColumns.filter((col) => col.showByDefault !== false);
    const defaultColumns = nonOptionalColumns.length > 0 ? nonOptionalColumns.map((col) => col.key) : newColumnsKeys;

    const savedColumnsRaw = this.loadFromLocalStorage<string[]>(storageKey);
    const savedOrderRaw = this.loadFromLocalStorage<string[]>(`${storageKey}_order`);

    // Ensure selected columns only include keys that still exist
    const savedColumns = (savedColumnsRaw || defaultColumns).filter((k) => newColumnsKeys.includes(k));
    // If no saved order exists, preserve the saved selection order and append new hidden columns
    const baseOrder = savedOrderRaw || savedColumns;
    const savedOrder = this.mergeOrder(baseOrder, newColumnsKeys);
    const savedCustomNames = this.loadFromLocalStorage<Record<string, string>>(`${storageKey}_custom_names`) || {};

    this.applyCustomNamesToColumns(newColumns, savedCustomNames);

    this.columns = newColumns;
    this.defaultColumns = defaultColumns;
    this.selectedColumns = savedColumns;
    this.tempSelectedColumns = [...savedColumns];
    this.columnOrder = savedOrder;
    this.isOpen = false;
    this.search = "";
    this.tableColumns = tableColumns;
    this.customColumnNames = savedCustomNames;
    this.originalColumnTitles = originalColumnTitles;
  }

  private loadFromLocalStorage<T>(key: string): T | null {
    const saved = localStorage.getItem(key);
    if (!saved) return null;

    try {
      return JSON.parse(saved) as T;
    } catch (e) {
      console.error(`Error parsing localStorage for key ${key}:`, e);
      return null;
    }
  }

  private saveToLocalStorage(key: string, data: any): void {
    localStorage.setItem(key, JSON.stringify(data));
  }

  private removeFromLocalStorage(...keys: string[]): void {
    keys.forEach((key) => localStorage.removeItem(key));
  }

  private mergeOrder(savedOrder: string[], allKeys: string[]): string[] {
    const uniqueSaved = savedOrder.filter((k) => allKeys.includes(k));
    const missing = allKeys.filter((k) => !uniqueSaved.includes(k));
    return [...uniqueSaved, ...missing];
  }

  private applyCustomNamesToColumns(columns: ColumnOption[], customNames: Record<string, string>): void {
    columns.forEach((col) => {
      if (customNames[col.key]) {
        col.label = customNames[col.key];
      }
    });
  }

  private mapTableColumns(tableColumns: TableColumn<any>[]): ColumnOption[] {
    return tableColumns
      .filter((col) => col.title || col.label)
      .map((col) => ({
        key: col.keyInsideItem,
        label: col.title || col.label || "",
        showByDefault: col.showByDefault,
      }));
  }

  get sortedColumns(): TableColumn<any>[] {
    if (!this.currentStorageKey) return [];
    return this.getSortedColumns();
  }

  @action
  getSortedColumns(): TableColumn<any>[] {
    if (!this.currentStorageKey || !this.tableColumns) return [];
    const filteredColumns = this.tableColumns.filter(Boolean).filter((col) => this.selectedColumns.includes(col.keyInsideItem));
    if (this.customColumnNames && Object.keys(this.customColumnNames).length > 0) {
      filteredColumns.forEach((col) => {
        if (this.customColumnNames[col.keyInsideItem]) {
          col.title = this.customColumnNames[col.keyInsideItem];
        }
      });
    }
    return sortBy(filteredColumns, (col) => this.columnOrder.indexOf(col.keyInsideItem));
  }

  getSearch(): string {
    return this.search;
  }

  @action
  setSearch(value: string): void {
    this.search = value;
  }

  getFilteredColumns(): ColumnOption[] {
    const filteredColumns = this.columns.filter((col) => col.label.toLowerCase().includes(this.search.toLowerCase()));
    return sortBy(filteredColumns, (col) => this.columnOrder.indexOf(col.key));
  }

  allFilteredSelected(): boolean {
    return this.getFilteredColumns().every((col) => this.tempSelectedColumns.includes(col.key));
  }

  noneFilteredSelected(): boolean {
    return this.getFilteredColumns().every((col) => !this.tempSelectedColumns.includes(col.key));
  }

  getTempSelectedColumns(): string[] {
    return this.tempSelectedColumns;
  }

  @action
  setTempSelectedColumns(columns: string[]): void {
    this.tempSelectedColumns = columns;
  }

  @action
  toggleColumn(key: string): void {
    if (this.tempSelectedColumns.includes(key)) {
      this.tempSelectedColumns = this.tempSelectedColumns.filter((k) => k !== key);
    } else {
      this.tempSelectedColumns = [...this.tempSelectedColumns, key];
    }
  }

  @action
  handleSelectAll(): void {
    const filteredColumnKeys = this.getFilteredColumns().map((col) => col.key);
    if (this.allFilteredSelected()) {
      this.tempSelectedColumns = this.tempSelectedColumns.filter((k) => !filteredColumnKeys.includes(k));
    } else {
      this.tempSelectedColumns = Array.from(new Set([...this.tempSelectedColumns, ...filteredColumnKeys]));
    }
  }

  @action
  open(): void {
    this.isOpen = true;
    this.tempSelectedColumns = [...this.selectedColumns];
  }

  @action
  close(): void {
    this.isOpen = false;
    this.search = "";
    this.tempSelectedColumns = [...this.selectedColumns];
  }

  @action
  confirm(): string[] {
    this.selectedColumns = [...this.tempSelectedColumns];
    this.isOpen = false;
    this.search = "";
    if (this.currentStorageKey) {
      const savedOrderRaw = this.loadFromLocalStorage<string[]>(`${this.currentStorageKey}_order`);
      const hasSavedOrder = Array.isArray(savedOrderRaw) && savedOrderRaw.length > 0;
      if (!hasSavedOrder) {
        const allColumnKeys = this.columns.map((col) => col.key);
        this.columnOrder = this.mergeOrder(this.selectedColumns, allColumnKeys);
      }
      this.saveToLocalStorage(this.currentStorageKey, this.selectedColumns);
    }
    return this.selectedColumns;
  }

  @action
  handleDragEnd(sourceIndex: number, destinationIndex: number): void {
    const filteredColumns = this.getFilteredColumns();
    const sourceKey = filteredColumns[sourceIndex].key;
    const destinationKey = filteredColumns[destinationIndex].key;
    const columnOrder = [...this.columnOrder];
    let sourceOrderIndex = columnOrder.indexOf(sourceKey);
    let destinationOrderIndex = columnOrder.indexOf(destinationKey);
    // If a key is not in the current order, append it so splicing operates on a valid index
    if (sourceOrderIndex === -1) {
      columnOrder.push(sourceKey);
      sourceOrderIndex = columnOrder.length - 1;
    }
    if (destinationOrderIndex === -1) {
      columnOrder.push(destinationKey);
      destinationOrderIndex = columnOrder.length - 1;
    }
    columnOrder.splice(sourceOrderIndex, 1);
    columnOrder.splice(destinationOrderIndex, 0, sourceKey);
    this.columnOrder = columnOrder;
    if (this.currentStorageKey) {
      this.saveToLocalStorage(`${this.currentStorageKey}_order`, columnOrder);
    }
  }

  @action
  updateColumnName(columnKey: string, newName: string): void {
    const column = this.columns.find((col) => col.key === columnKey);
    if (column) {
      column.label = newName;
    }
    this.customColumnNames[columnKey] = newName;
    if (this.currentStorageKey) {
      this.saveToLocalStorage(`${this.currentStorageKey}_custom_names`, this.customColumnNames);
    }
  }

  @action
  resetToDefault(): void {
    this.selectedColumns = [...this.defaultColumns];
    this.tempSelectedColumns = [...this.defaultColumns];
    // Reset order to contain all columns in their original order
    this.columnOrder = this.columns.map((c) => c.key);
    this.customColumnNames = {};
    if (this.tableColumns) {
      this.tableColumns.forEach((col) => {
        col.title = this.originalColumnTitles[col.keyInsideItem] || "";
      });
    }
    this.columns = this.mapTableColumns(this.tableColumns || []);
    this.getSortedColumns();
    if (this.currentStorageKey) {
      this.removeFromLocalStorage(this.currentStorageKey, `${this.currentStorageKey}_order`, `${this.currentStorageKey}_custom_names`);
    }
  }
}
